<script lang="ts">
    const component = {
        name: 'NodeSegmentContactsPreview',
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    import { reactive, computed, inject } from 'vue';
    import { SegmentDefinition, SegmentPreviewResult } from '@cwd/Views/Segments/interfaces';
    import { getSegmentForPreview } from '@journeys/utils/previewNodeSegment';
    import { loadSegmentPreview } from '@cwd/Views/Segments/services/requests';
    import Modal from '@shared/components/Modal.vue';
    import List from '@shared/components/List/List.vue';
    import ContactListItem from '@views/Lists/ContactListItem.vue';
    import Contact from '@views/Contact/Contact.vue';
    import ContactAdditionalFieldsDropdown from '@views/Lists/components/ContactAdditionalFieldsDropdown.vue';
    import { useContactAdditionalFields } from '@views/Lists/useContactAdditionalFields';
    import { formatNumber } from '@cwd/Shared/utils/formatNumber';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';

    const props = defineProps<{
        // the node's fully computed step segment (same one CalculateSegmentReach uses) - it
        // includes the RecipientSegment/SmsRecipientSegment "not yet received" condition and
        // delay/TimeSpanSince conditions, so the preview only lists contacts who will actually
        // get the issue/sms on the next run, not everyone who has ever matched the node's segment
        segmentDefinition: SegmentDefinition;
        nodeId: number;
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const { availableFields, selectedFieldNames, selectedFields, toggleField } = useContactAdditionalFields(contextAccess, { hideAddedDateTime: true });

    const state = reactive({
        show: false,
        loading: false,
        result: null as SegmentPreviewResult | null,
        pageIndex: 0,
        filter: '',
        contactId: null as string | null,
    });

    const allLoaded = computed(() => state.result?.Result.length === state.result?.Count);

    const load = async (resetPaging = false) => {
        if (resetPaging) {
            state.pageIndex = 0;
            state.result = null;
        }

        state.loading = true;

        try {
            const data = await loadSegmentPreview(getSegmentForPreview(props.segmentDefinition), state.filter, state.pageIndex);
            state.result = state.pageIndex > 0 && state.result ? { ...data, Result: [...state.result.Result, ...data.Result] } : data;
        } finally {
            state.loading = false;
        }
    };

    const open = () => {
        state.show = true;
        state.contactId = null;
        load(true);
    };

    const showContact = (contactId: string) => {
        state.contactId = contactId;
    };

    const closeContact = () => {
        state.contactId = null;
    };

    const loadMore = () => {
        state.pageIndex += 1;
        load();
    };

    const updateFilter = (newFilter: string) => {
        state.filter = newFilter;
        load(true);
    };

    const segmentPreviewListHeaderText = () => {
        if (!state.result) return '';
        return (
            $Resources.SegmentPreviewContactsHeading.replace('{{count}}', formatNumber(state.result.Result.length)) +
            ' (' +
            formatNumber(state.result.Count) +
            ')'
        );
    };
</script>

<template>
    <button type="button" class="btn btn-default text-wrap" @click="open">{{ $Resources.NodeSegmentContactsPreviewButton }}</button>

    <Modal :title="$Resources.NodeSegmentContactsPreviewTitle" :show="state.show" @close="state.show = false" size="xl">
        <template #content>
            <Contact v-if="state.contactId" :contactId="state.contactId" :backBtn="true" @goBack="closeContact" />

            <template v-else>
                <p>
                    {{ segmentPreviewListHeaderText() }}
                </p>

                <List
                    itemId="ContactId"
                    :items="state.result?.Result"
                    :all-loaded="allLoaded"
                    :loading="state.loading"
                    :server-side-filter="true"
                    :filterRateLimit="500"
                    @load-more="loadMore"
                    @update:filter="updateFilter">
                    <template #toolbar>
                        <ContactAdditionalFieldsDropdown
                            :availableFields="availableFields"
                            :selectedFieldNames="selectedFieldNames"
                            @toggleField="toggleField" />
                    </template>
                    <template #item="{ item }">
                        <ContactListItem :contact="item" :selectedFields="selectedFields" @navigate-to-contact="showContact(item.ContactId)" />
                    </template>
                </List>
            </template>
        </template>
    </Modal>
</template>

